/**
 * @module
 */

import {guard, OBJECT, OPTIONAL, BOOLEAN, INTERFACE, IS_INPUT, OR, NULL} from '@acng/frontend-rubicon';
import {popup} from '@acng/frontend-discovery';
import {FormController, setMailAddressInput} from 'nova';
import {setPasswordInput, setUsernameInput} from 'nova';
/** @import {FormArrangement, SubmitAction} from 'nova'; */

import {post} from '../../core/service/backend.js';
import {inject} from '../../core/service/ng.js';
import {getLocalOverlay} from '../../core/service/overlay.js';
import {t} from '../../locale/config/translate.js';
import {ConfirmMail} from './mail-confirm.js';
import {AUTH_USER_DATA} from '../model/user-data.js';
import {performLogin} from './login.js';

const FIELDS = /* @__PURE__ */ INTERFACE({
  nickname: IS_INPUT,
  email: IS_INPUT,
  password: IS_INPUT,
  termsAccepted: IS_INPUT,
});

class UserSignup extends FormController {
  /**
   * Send the confirm mail again.
   */
  mail = new ConfirmMail();

  /**
   * | name          | type     | autocomplete | validation |
   * | ------------- | -------- | ------------ | ---------- |
   * | nickname      | text     | username     | required   |
   * | email         | email    | email        | required   |
   * | password      | password | new-password | required   |
   * | termsAccepted | checkbox | off          | required   |
   *
   * The "password" field is optional.
   *
   * @type {FormArrangement}
   */
  arrange(form) {
    ASSERT: guard(form.elements, FIELDS);
    const {nickname, email, password, termsAccepted} = form.elements;

    setUsernameInput(nickname);
    setMailAddressInput(email);

    if (password) {
      setPasswordInput(password);
    }

    termsAccepted.required = true;
    termsAccepted.type = 'checkbox';
  }

  /**
   * @type {SubmitAction}
   */
  async submit(form, data) {
    const response = await post('user/signup', data, form);

    ASSERT: {
      const RESPONSE = OR(
        OBJECT({
          user: AUTH_USER_DATA,
        }),
        OBJECT({
          user: NULL,
          reactivation_mail_sent: OPTIONAL(BOOLEAN),
          doi_hint: OPTIONAL(BOOLEAN),
        }),
      );

      guard(response, RESPONSE);
    }

    if ('doi_hint' in response) {
      getLocalOverlay(form)?.close();
      inject('onsOverlay').create('doiHint').disableBackdrop().disableCloseButtonInToolbar().open();

      return;
    }

    if ('reactivation_mail_sent' in response) {
      await popup(form).warn(await t('userPool.reactivationMailSent'));

      return;
    }

    getLocalOverlay(form)?.close();
    performLogin(response.user);
  }
}

export {UserSignup};
